import * as Actions from '../actions/holidayCalendarActions';
import HolidayCalendar from '../../entities/holidayCalendar';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type HolidayCalendarAction = { 
    type: string; 
    holidayCalendar: HolidayCalendar | null; 
    holidayCalendarId: string | HolidayCalendar; 
    partial: boolean; 
    lastFetch: number; 
    holidayCalendars: HolidayCalendar[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<HolidayCalendar> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {HolidayCalendar[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<HolidayCalendar> | null, firmId: string, userId: string, updates?: HolidayCalendar[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'holidayCalendar', 'holidayCalendars', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<HolidayCalendar>} data
 * @param {HolidayCalendar[]} [updates]
 * @returns {ArrayMap<HolidayCalendar>}
 */
function updateStorage(data: ArrayMap<HolidayCalendar>, updates?: HolidayCalendar[]): ArrayMap<HolidayCalendar> {
	if (logging) console.debug('⮂ holidayCalendarReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {HolidayCalendar[]} [updates]
 * @returns {ArrayMap<HolidayCalendar>}
 */
function updateStorageNoReturn(updates?: HolidayCalendar[]): void {
	if (logging) console.debug('⮂ holidayCalendarReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {HolidayCalendar[]} [data]
 * @returns {ArrayMap<HolidayCalendar>}
 */
function createState(data?: HolidayCalendar[] | ArrayMap<HolidayCalendar>): ArrayMap<HolidayCalendar> {
	if (!data) return ArrayMapRegistry.clone<HolidayCalendar>('holidayCalendar', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<HolidayCalendar>('holidayCalendar', data.map(e => e));
    else return ArrayMapRegistry.clone<HolidayCalendar>('holidayCalendar', data);
}

function holidayCalendars(state: ArrayMap<HolidayCalendar>, action: HolidayCalendarAction): ArrayMap<HolidayCalendar> {
	if (!state) state = createState();

	let st: ArrayMap<HolidayCalendar>;

	switch (action.type) {
		// action.holidayCalendar - the new value of holidayCalendar with id action.id
		// action.holidayCalendarId - id of the target, used if new value is null
		case Actions.HOLIDAYCALENDAR_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ holidayCalendarReducer: FETCH_ONE_SUCCESS', action);

			if (action.holidayCalendar === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.holidayCalendarId)) {
				st = state.clone();
				st.set(action.holidayCalendar);
				// state.clear();

				updateStorageNoReturn([action.holidayCalendar]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.holidayCalendars
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.holidayCalendars
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.holidayCalendars
			 !partial, !stateEmpty, fetchEmpty	-> D return holidayCalendars in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge holidayCalendars in state newer than those in action.holidayCalendars
		 */
		case Actions.HOLIDAYCALENDAR_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.holidayCalendars.length === 0;
			const fetched = action.holidayCalendars.map(holidayCalendar => new HolidayCalendar({...holidayCalendar, persisted: true}));

			if (logging) console.debug(`⮂ holidayCalendarReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} holidayCalendars with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ holidayCalendarReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ holidayCalendarReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<HolidayCalendar> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: HolidayCalendar[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(holidayCalendar => {
					const current = st.get(holidayCalendar.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && holidayCalendar.status === 'D') {
							// Remove it from state
							toUpdate.push(holidayCalendar);
							st.delete(holidayCalendar);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (holidayCalendar.addedOn > current.addedOn) {
								toUpdate.push(holidayCalendar);
								st.set(holidayCalendar);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (holidayCalendar.status !== 'D') {
							toUpdate.push(holidayCalendar);
							st.set(holidayCalendar);
						}
						else if (logging) console.debug('⮂ holidayCalendarReducer: partialMerge: skipping deleted holidayCalendar not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ holidayCalendarReducer: empty: holidayCalendars', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ holidayCalendarReducer: fullMerge: holidayCalendars', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localHolidayCalendar => {
                    // If this holidayCalendar has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localHolidayCalendar.persisted) {
                        const fetchedHolidayCalendar = st.get(localHolidayCalendar.id);
                        if (!fetchedHolidayCalendar || localHolidayCalendar.addedOn > fetchedHolidayCalendar.addedOn) st.set(localHolidayCalendar);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.HOLIDAYCALENDAR_FETCH_ONE_FAILURE:
			console.error('HOLIDAYCALENDAR_FETCH_ONE_FAILURE', action.holidayCalendarId, action.err);

			// No update to storage required
			return state;

		case Actions.HOLIDAYCALENDAR_FETCH_FAILURE:
			console.error('HOLIDAYCALENDAR_FETCH_FAILURE', action.holidayCalendarId, action.err);

			// No update to storage required
			return state;

		case Actions.HOLIDAYCALENDAR_RESTORE: {
		    let holidayCalendar = new HolidayCalendar({...action.holidayCalendar});
            st = state.clone();

            const existing = st.get(holidayCalendar.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (holidayCalendar.addedOn >= existing.addedOn) {
                    st.set(holidayCalendar);
                    return updateStorage(st, [holidayCalendar]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (holidayCalendar.status === 'D') return state;

                st.set(holidayCalendar);
                return updateStorage(st, [holidayCalendar]);
            }
        }
            
		case Actions.HOLIDAYCALENDAR_SAVE: {
		    // console.debug('⮂ HOLIDAYCALENDAR_SAVE reducer', action.holidayCalendar?.id);
		    
			// clone the holidayCalendar
			let holidayCalendar = new HolidayCalendar({...action.holidayCalendar});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new holidayCalendar in the state
			// If this is a create, add to the queue
			const _holidayCalendar = st.get(holidayCalendar.id);

			if (!_holidayCalendar) st.set(holidayCalendar);
			else {
				if (holidayCalendar.addedOn >= _holidayCalendar.addedOn) st.set(holidayCalendar);
			}

			return updateStorage(st, [holidayCalendar]);
		}

		case Actions.HOLIDAYCALENDAR_SAVE_MANY: {
		    if (logging) console.debug('⮂ HOLIDAYCALENDAR_SAVE_MANY reducer', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));
		    
			// clone the holidayCalendars
			let toProcess = action.holidayCalendars.map(holidayCalendar => new HolidayCalendar(holidayCalendar));

			st = state.clone();
			// state.clear();

			const updates: HolidayCalendar[] = [];

			toProcess.forEach(holidayCalendar => {
				// Add or update the new holidayCalendar in the state
				// If this is a create, add to the queue
				const existing = st.get(holidayCalendar.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (holidayCalendar.status !== 'D') {
						st.push(holidayCalendar);
						updates.push(holidayCalendar);
					}
					else if (logging) console.debug('⮂ HOLIDAYCALENDAR_SAVE_MANY: skipping deleted holidayCalendar not in state');
				}
				else {
					if (holidayCalendar.addedOn >= existing.addedOn) {
						st.set(holidayCalendar);
						updates.push(holidayCalendar);
					}
					else if (logging) console.debug('⮂ Skipping, existing holidayCalendar had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.HOLIDAYCALENDAR_REMOVE: {
			// clone the holidayCalendar
			const holidayCalendar = new HolidayCalendar({...action.holidayCalendar});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(holidayCalendar);

			return updateStorage(st, [holidayCalendar]);
		}

		case Actions.HOLIDAYCALENDAR_RESTORE_FAILURE:
		case Actions.HOLIDAYCALENDAR_SAVE_FAILURE: {
		    console.warn('HOLIDAYCALENDAR_SAVE_FAILURE reducer', action.holidayCalendar?.id);
		    if (!action.holidayCalendar?.id) {
				console.warn('HOLIDAYCALENDAR_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const holidayCalendar = ensure(HolidayCalendar, action.holidayCalendar);
			if (!holidayCalendar) {
				console.error('Could not find holidayCalendar after call to save');
				return state;
			}

			st = state.clone();

			const _holidayCalendarRef = state.get(holidayCalendar.id);

			if (_holidayCalendarRef && (_holidayCalendarRef.addedOn === holidayCalendar.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _holidayCalendar = new HolidayCalendar({..._holidayCalendarRef});
				_holidayCalendar.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_holidayCalendar.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('holidayCalendar', _holidayCalendar).then(r => {
						if (r.isErr()) console.error(`⮂ holidayCalendarReducer: failed to save holidayCalendar with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ holidayCalendarReducer: saved holidayCalendar with fatalError info');
					}).catch(err => console.error(`⮂ holidayCalendarReducer: failed to save holidayCalendar with fatalError info: ${err.message}`));

				st.set(_holidayCalendar);
			}

			return updateStorage(st);
		}

		case Actions.HOLIDAYCALENDAR_SAVE_MANY_FAILURE: {
		    console.warn('HOLIDAYCALENDAR_SAVE_MANY_FAILURE reducer', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.HOLIDAYCALENDAR_RESTORE_SUCCESS:
		case Actions.HOLIDAYCALENDAR_SAVE_SUCCESS: {
			if (!action.holidayCalendar?.id) {
				console.warn('HOLIDAYCALENDAR_SAVE_SUCCESS reducer');
                return state;
			}

			const holidayCalendarRef = state.get(action.holidayCalendar?.id);
			if (!holidayCalendarRef) {
				console.warn('Could not find holidayCalendar after call to save');
                return state;
			}

			st = state.clone();

			if (action.holidayCalendar.addedOn >= holidayCalendarRef.addedOn) {
				st.set(action.holidayCalendar);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const holidayCalendar = new HolidayCalendar({...holidayCalendarRef, persisted: true});
				st.set(holidayCalendar);
			}

			return updateStorage(st, [action.holidayCalendar]);
		}

		case Actions.HOLIDAYCALENDAR_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ HOLIDAYCALENDAR_SAVE_MANY_SUCCESS reducer', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const holidayCalendars: HolidayCalendar[] = [];

			action.holidayCalendars.forEach(e => {
				const holidayCalendarRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!holidayCalendarRef) console.warn('Could not find holidayCalendar after call to save');
				else if (e.addedOn >= holidayCalendarRef.addedOn) {
				    st.set(e);
				    holidayCalendars.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const holidayCalendar = new HolidayCalendar({...holidayCalendarRef, persisted: true});
					st.set(holidayCalendar);
					holidayCalendars.push(holidayCalendar);
				}
			});

			return updateStorage(st, holidayCalendars);
		}

		case Actions.HOLIDAYCALENDAR_REMOVE_FAILURE: {
		    console.warn('HOLIDAYCALENDAR_REMOVE_FAILURE action', action.holidayCalendar?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.HOLIDAYCALENDAR_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.HOLIDAYCALENDAR_RESTORE_MANY: {
			if (logging) console.debug('⮂ HOLIDAYCALENDAR_RESTORE_MANY reducer', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));

			const toProcess = action.holidayCalendars.map(holidayCalendar => new HolidayCalendar({...holidayCalendar}));
			st = state.clone();

			const updates: HolidayCalendar[] = [];

			toProcess.forEach(holidayCalendar => {
				const existing = st.get(holidayCalendar.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (holidayCalendar.addedOn >= existing.addedOn) {
						st.set(holidayCalendar);
						updates.push(holidayCalendar);
					}
					else if (logging) console.debug('⮂ HOLIDAYCALENDAR_RESTORE_MANY: skipping older version of holidayCalendar');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (holidayCalendar.status !== 'D') {
						st.set(holidayCalendar);
						updates.push(holidayCalendar);
					}
					else if (logging) console.debug('⮂ HOLIDAYCALENDAR_RESTORE_MANY: skipping deleted holidayCalendar not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.HOLIDAYCALENDAR_REMOVE_MANY: {
			if (logging) console.debug('⮂ HOLIDAYCALENDAR_REMOVE_MANY reducer', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));

			const toProcess = action.holidayCalendars.map(holidayCalendar => new HolidayCalendar({...holidayCalendar}));
			st = state.clone();

			toProcess.forEach(holidayCalendar => {
				st.delete(holidayCalendar);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default holidayCalendars;